import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../api/client';
import { PageLoader } from './PageLoader';
import { formatIsoDate } from '../lib/dateFormat';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { formatTHB, toDecimalString, toMicro } from '../lib/money';

const WEEKS = 53;
const DAY_MS = 86_400_000;

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function addDays(dateStr: string, n: number): string {
  return isoDate(new Date(new Date(`${dateStr}T00:00:00Z`).getTime() + n * DAY_MS));
}
function rangeStart(dateStr: string): string {
  return `${dateStr}T00:00:00Z`;
}
function rangeEnd(dateStr: string): string {
  return `${dateStr}T23:59:59Z`;
}

type Day = { date: string; costMicro: bigint };

/**
 * A GitHub-style contribution graph for spend, plus the streak/average stats
 * derived from the same data. No charting library — a CSS grid of divs, same
 * philosophy as BarList.tsx.
 *
 * Mounted only from Dashboard's non-onboarding branch, so `ready`/`hasOrg`
 * mirror the gate Dashboard already applies to its own usage fetch. A 403
 * here is swallowed rather than shown: Dashboard's own fetch hits the same
 * wall and swaps the whole page to OnboardingNotice, so this panel has
 * nothing useful to say on its own before it unmounts.
 */
export function ActivityHeatmap({ ready, hasOrg }: { ready: boolean; hasOrg: boolean }) {
  const { dict, locale } = useLanguage();
  const [byDate, setByDate] = useState<Map<string, bigint> | null>(null);
  const [totalMicro, setTotalMicro] = useState<bigint>(0n);
  const [error, setError] = useState<string | null>(null);

  const today = useMemo(() => isoDate(new Date()), []);
  // Aligned to a Sunday so week columns line up cleanly under month labels.
  const start = useMemo(() => {
    const raw = addDays(today, -(WEEKS * 7 - 1));
    return addDays(raw, -new Date(`${raw}T00:00:00Z`).getUTCDay());
  }, [today]);

  useEffect(() => {
    if (!ready || !hasOrg) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.usageSummary({ from: rangeStart(start), to: rangeEnd(today), group_by: 'day' });
        if (cancelled) return;
        const map = new Map<string, bigint>();
        for (const b of res.buckets) map.set(b.label, toMicro(b.cost.amount));
        setByDate(map);
        setTotalMicro(toMicro(res.total.amount));
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) return;
        setError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.dashboard.activity.loadError);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, hasOrg, start, today, dict]);

  const weeks = useMemo(() => {
    const cols: (Day | null)[][] = [];
    let cursor = start;
    for (let w = 0; w < WEEKS; w++) {
      const col: (Day | null)[] = [];
      for (let d = 0; d < 7; d++) {
        col.push(cursor > today ? null : { date: cursor, costMicro: byDate?.get(cursor) ?? 0n });
        cursor = addDays(cursor, 1);
      }
      cols.push(col);
    }
    return cols;
  }, [start, today, byDate]);

  const flatDays = useMemo(
    () => weeks.flat().filter((c): c is Day => c !== null),
    [weeks],
  );

  const maxMicro = useMemo(() => flatDays.reduce((m, d) => (d.costMicro > m ? d.costMicro : m), 0n), [flatDays]);
  const loading = byDate === null;
  const empty = !loading && !error && totalMicro <= 0n && maxMicro <= 0n;

  function level(costMicro: bigint): number {
    if (costMicro <= 0n || maxMicro <= 0n) return 0;
    const ratio = Number((costMicro * 4n) / maxMicro);
    return Math.min(4, Math.max(1, ratio));
  }

  const stats = useMemo(() => {
    let longest = 0;
    let current = 0;
    for (const d of flatDays) {
      if (d.costMicro > 0n) {
        current += 1;
        longest = Math.max(longest, current);
      } else {
        current = 0;
      }
    }
    const n = BigInt(flatDays.length || 1);
    return { longest, avgDay: totalMicro / n, avgWeek: (totalMicro * 7n) / n };
  }, [flatDays, totalMicro]);

  const monthFmt = useMemo(
    () => new Intl.DateTimeFormat(locale === 'th' ? 'th-TH' : 'en-US', { month: 'short', timeZone: 'UTC' }),
    [locale],
  );
  let lastMonth = '';
  const monthLabels = weeks.map((col) => {
    const first = col[0];
    if (!first) return '';
    const label = monthFmt.format(new Date(`${first.date}T00:00:00Z`));
    if (label === lastMonth) return '';
    lastMonth = label;
    return label;
  });

  const summaryLabel = empty
    ? dict.dashboard.activity.emptyTitle
    : dict.dashboard.activity.summary(formatTHB(toDecimalString(totalMicro)), stats.longest);

  return (
    <div className="panel activity-panel">
      <div className="panel-head">
        <h2>{dict.dashboard.activity.heading}</h2>
        <Link to="/usage" className="panel-head-link">
          {dict.dashboard.viewAll}
        </Link>
      </div>
      {error && <div className="err">{error}</div>}

      {loading ? (
        <PageLoader />
      ) : (
        <>
      <div className="activity-stats">
        <div className="activity-stat">
          <span className="activity-stat-label">{dict.dashboard.activity.longestStreak}</span>
          <span className="activity-stat-value">{dict.dashboard.activity.days(stats.longest)}</span>
        </div>
        <div className="activity-stat">
          <span className="activity-stat-label">{dict.dashboard.activity.avgPerDay}</span>
          <span className="activity-stat-value">{formatTHB(toDecimalString(stats.avgDay))}</span>
        </div>
        <div className="activity-stat">
          <span className="activity-stat-label">{dict.dashboard.activity.avgPerWeek}</span>
          <span className="activity-stat-value">{formatTHB(toDecimalString(stats.avgWeek))}</span>
        </div>
        <div className="activity-stat">
          <span className="activity-stat-label">{dict.dashboard.activity.total}</span>
          <span className="activity-stat-value">{formatTHB(toDecimalString(totalMicro))}</span>
        </div>
      </div>

      {empty && <p className="activity-empty note">{dict.dashboard.activity.emptyBody}</p>}

      <div className="activity-graph">
        <div className="activity-months" style={{ gridTemplateColumns: `repeat(${WEEKS}, 1fr)` }} aria-hidden="true">
          {monthLabels.map((label, i) => (
            <span key={i}>{label}</span>
          ))}
        </div>
        <div className="activity-graph-body">
          <div className="activity-weekdays" aria-hidden="true">
            {/* Sun..Sat — labels only on Mon/Wed/Fri, same density as GitHub's graph. */}
            {[
              null,
              dict.dashboard.activity.weekdayMon,
              null,
              dict.dashboard.activity.weekdayWed,
              null,
              dict.dashboard.activity.weekdayFri,
              null,
            ].map((label, i) => (
              <span key={i}>{label}</span>
            ))}
          </div>
          <div
            className="activity-grid"
            style={{ gridTemplateColumns: `repeat(${WEEKS}, 1fr)` }}
            role="img"
            aria-label={summaryLabel}
          >
            {weeks.map((col, ci) =>
              col.map((day, ri) =>
                day ? (
                  <span
                    key={`${ci}-${ri}`}
                    className={`activity-cell level-${level(day.costMicro)}`}
                    title={`${formatIsoDate(day.date)} · ${formatTHB(toDecimalString(day.costMicro))}`}
                    aria-hidden="true"
                  />
                ) : (
                  <span key={`${ci}-${ri}`} className="activity-cell activity-cell--empty" aria-hidden="true" />
                ),
              ),
            )}
          </div>
        </div>
      </div>

      <div className="activity-legend" aria-hidden="true">
        <span>{dict.dashboard.activity.less}</span>
        {[0, 1, 2, 3, 4].map((l) => (
          <span key={l} className={`activity-cell level-${l}`} />
        ))}
        <span>{dict.dashboard.activity.more}</span>
      </div>
        </>
      )}
    </div>
  );
}
